// components/PortfolioReviewPage.jsx - Portfolio Review 完整页面
import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../theme';
import { useAuth } from '../AuthContext';
import { MetricsDisplay } from '../Metricsdisplay';
import { addPortfolioHistory } from '../firebase';
import { createChart, CandlestickSeries, AreaSeries } from 'lightweight-charts';

// ==================== K线图组件 ====================
function StockChart({ symbol, candles, sentimentZones, tradeMarkers, analysis }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [markerPositions, setMarkerPositions] = useState([]);

  useEffect(() => {
    if (!chartContainerRef.current || !candles || candles.length === 0) return;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: { background: { color: '#0d1117' }, textColor: '#8b949e' },
      grid: { vertLines: { color: '#21262d' }, horzLines: { color: '#21262d' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#30363d' },
      timeScale: { borderColor: '#30363d', timeVisible: true, dayVisible: true },
    });

    chartRef.current = chart;

    const candleData = candles.map(c => ({ time: c.date, open: c.open, high: c.high, low: c.low, close: c.close }));

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#22c55e', downColor: '#f85149', borderDownColor: '#f85149', borderUpColor: '#22c55e', wickDownColor: '#f85149', wickUpColor: '#22c55e',
    });
    candleSeries.setData(candleData);

    if (sentimentZones && sentimentZones.length > 0) {
      const bullishSeries = chart.addSeries(AreaSeries, { topColor: 'rgba(34, 197, 94, 0.3)', bottomColor: 'rgba(34, 197, 94, 0.0)', lineColor: 'rgba(34, 197, 94, 0)', lineWidth: 0 });
      const bullishData = candles.map((c, i) => {
        let isBullish = false;
        for (const zone of sentimentZones) { if (i >= zone.start_index && i <= zone.end_index && zone.sentiment === 'bullish') { isBullish = true; break; } }
        return { time: c.date, value: isBullish ? c.high * 1.05 : 0 };
      });
      bullishSeries.setData(bullishData);

      const bearishSeries = chart.addSeries(AreaSeries, { topColor: 'rgba(168, 85, 247, 0.3)', bottomColor: 'rgba(168, 85, 247, 0.0)', lineColor: 'rgba(168, 85, 247, 0)', lineWidth: 0 });
      const bearishData = candles.map((c, i) => {
        let isBearish = false;
        for (const zone of sentimentZones) { if (i >= zone.start_index && i <= zone.end_index && zone.sentiment === 'bearish') { isBearish = true; break; } }
        return { time: c.date, value: isBearish ? c.high * 1.05 : 0 };
      });
      bearishSeries.setData(bearishData);
    }

    // 计算markers位置
    const updateMarkerPositions = () => {
      if (!tradeMarkers || tradeMarkers.length === 0) {
        console.log('No trade markers');
        return;
      }

      const timeScale = chart.timeScale();
      const positions = [];

      console.log('Updating markers, tradeMarkers:', tradeMarkers);

      tradeMarkers.forEach(t => {
        const x = timeScale.timeToCoordinate(t.date);
        console.log(`Marker ${t.date}: x=${x}`);
        if (x === null || x === undefined) return;

        const candle = candles.find(c => c.date === t.date);
        console.log(`Candle found:`, candle);
        if (!candle) return;

        const price = t.type === 'buy' ? candle.low : candle.high;
        const y = candleSeries.priceToCoordinate(price);
        console.log(`y=${y}, type=${t.type}`);
        if (y === null || y === undefined) return;

        positions.push({ x, y, type: t.type });
      });

      console.log('Final positions:', positions);
      setMarkerPositions(positions);
    };

    setTimeout(updateMarkerPositions, 100);
    chart.timeScale().subscribeVisibleLogicalRangeChange(updateMarkerPositions);

    const handleResize = () => {
      if (chartContainerRef.current) chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      updateMarkerPositions();
    };
    window.addEventListener('resize', handleResize);
    chart.timeScale().fitContent();

    return () => { window.removeEventListener('resize', handleResize); if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; } };
  }, [candles, sentimentZones, tradeMarkers]);

  const currentPrice = candles && candles.length > 0 ? candles[candles.length - 1].close : 0;
  const priceChange = candles && candles.length > 1 ? ((candles[candles.length - 1].close - candles[0].close) / candles[0].close * 100).toFixed(2) : 0;

  return (
    <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h3 style={{ fontFamily: 'Georgia, serif', color: theme.text, fontSize: '28px', fontWeight: 400, margin: 0 }}>{symbol}</h3>
          <span style={{ color: theme.textMuted, fontSize: '18px' }}>${currentPrice.toFixed(2)}</span>
          <span style={{ color: priceChange >= 0 ? theme.green : theme.red, fontSize: '16px', fontWeight: 600 }}>{priceChange >= 0 ? '+' : ''}{priceChange}%</span>
        </div>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#22d3ee', fontSize: '16px' }}>▲</span><span style={{ color: theme.textMuted, fontSize: '14px' }}>Bought</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: '#a855f7', fontSize: '16px' }}>▼</span><span style={{ color: theme.textMuted, fontSize: '14px' }}>Sold</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '12px', height: '12px', background: 'rgba(34, 197, 94, 0.5)', borderRadius: '2px' }}></span><span style={{ color: theme.textMuted, fontSize: '14px' }}>Bullish</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '12px', height: '12px', background: 'rgba(168, 85, 247, 0.5)', borderRadius: '2px' }}></span><span style={{ color: theme.textMuted, fontSize: '14px' }}>Bearish</span></div>
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        <div ref={chartContainerRef} style={{ width: '100%', height: '400px' }} />
        {/* Markers Overlay */}
        <div style={{ position: 'absolute', top: 0, left: 60, right: 50, bottom: 30, pointerEvents: 'none', zIndex: 10 }}>
          {markerPositions.map((m, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: m.x - 10,
                top: m.type === 'buy' ? m.y + 5 : m.y - 22,
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                ...(m.type === 'buy'
                  ? { borderBottom: '16px solid #22d3ee' }
                  : { borderTop: '16px solid #a855f7' })
              }}
            />
          ))}
        </div>
      </div>
      {analysis && (
        <div style={{ marginTop: '20px', padding: '20px', background: theme.bg, borderRadius: '10px', borderLeft: '4px solid ' + theme.teal }}>
          <h4 style={{ color: theme.teal, fontSize: '14px', fontWeight: 600, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '1px' }}>AI Analysis</h4>
          <StockAnalysisContent analysis={analysis} />
        </div>
      )}
    </div>
  );
}

function StockAnalysisContent({ analysis }) {
  if (!analysis) return null;
  let entryAnalysis = '', timingScore = '', improvement = '';
  const lines = analysis.split('\n');
  let currentSection = '';
  for (const line of lines) {
    const cleanLine = line.replace(/[📊⏱️💡🎯📍🤖🔍📈]/g, '').trim();
    if (cleanLine.includes('ENTRY ANALYSIS')) currentSection = 'entry';
    else if (cleanLine.includes('TIMING SCORE')) currentSection = 'timing';
    else if (cleanLine.includes('IMPROVEMENT') || cleanLine.includes('POSITION STATUS')) currentSection = 'improvement';
    else if (cleanLine && currentSection === 'entry') entryAnalysis += cleanLine + ' ';
    else if (cleanLine && currentSection === 'timing') timingScore += cleanLine + ' ';
    else if (cleanLine && currentSection === 'improvement') improvement += cleanLine + ' ';
  }
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
        <div style={{ background: theme.bgCard, borderRadius: '8px', padding: '16px' }}><h4 style={{ color: theme.text, fontSize: '16px', fontWeight: 600, margin: '0 0 10px', textTransform: 'uppercase' }}>Entry Analysis</h4><p style={{ color: theme.textMuted, fontSize: '17px', lineHeight: '1.7', margin: 0 }}>{entryAnalysis.trim() || 'N/A'}</p></div>
        <div style={{ background: theme.bgCard, borderRadius: '8px', padding: '16px' }}><h4 style={{ color: theme.text, fontSize: '16px', fontWeight: 600, margin: '0 0 10px', textTransform: 'uppercase' }}>Timing Score</h4><p style={{ color: theme.textMuted, fontSize: '17px', lineHeight: '1.7', margin: 0 }}>{timingScore.trim() || 'N/A'}</p></div>
      </div>
      {improvement && <div style={{ background: theme.bgCard, borderRadius: '8px', padding: '16px' }}><h4 style={{ color: theme.text, fontSize: '16px', fontWeight: 600, margin: '0 0 10px', textTransform: 'uppercase' }}>Improvement</h4><p style={{ color: theme.textMuted, fontSize: '17px', lineHeight: '1.7', margin: 0 }}>{improvement.trim()}</p></div>}
    </div>
  );
}

function OverallAnalysisContent({ analysis }) {
  if (!analysis) return null;
  let tradingBehavior = '', timingAnalysis = '', keyIssues = [], suggestions = [];
  const lines = analysis.split('\n');
  let currentSection = '';
  for (const line of lines) {
    const cleanLine = line.replace(/[📊⏱️💡🎯📍🤖⚠️]/g, '').trim();
    if (cleanLine.includes('TRADING BEHAVIOR')) currentSection = 'trading';
    else if (cleanLine.includes('TIMING ANALYSIS')) currentSection = 'timing';
    else if (cleanLine.includes('KEY ISSUES') || cleanLine.includes('PROBLEM AREAS')) currentSection = 'issues';
    else if (cleanLine.includes('SUGGESTIONS') || cleanLine.includes('ACTIONABLE')) currentSection = 'suggestions';
    else if (cleanLine && currentSection === 'trading') tradingBehavior += cleanLine + ' ';
    else if (cleanLine && currentSection === 'timing') timingAnalysis += cleanLine + ' ';
    else if (cleanLine && currentSection === 'issues') {
      // 分割成独立的 issue 项
      const cleaned = cleanLine.replace(/^\d+\.\s*/, '').replace(/\*\*/g, '');
      if (cleaned.length > 10) keyIssues.push(cleaned);
    }
    else if (cleanLine && currentSection === 'suggestions') {
      const cleaned = cleanLine.replace(/^\d+\.\s*/, '').replace(/\*\*/g, '').replace(/:\s*:/, ':');
      if (cleaned.length > 10) suggestions.push(cleaned);
    }
  }

  // 如果 keyIssues 只有一个长字符串，尝试按数字分割
  if (keyIssues.length === 1 && keyIssues[0].includes('. ')) {
    const splitIssues = keyIssues[0].split(/\d+\.\s*/).filter(s => s.trim().length > 10);
    if (splitIssues.length > 1) keyIssues = splitIssues;
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        <div style={{ background: theme.bg, borderRadius: '10px', padding: '24px' }}>
          <h4 style={{ color: theme.text, fontSize: '16px', fontWeight: 600, margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trading Behavior</h4>
          <p style={{ color: theme.textMuted, fontSize: '17px', lineHeight: '1.8', margin: 0 }}>{tradingBehavior.trim() || 'N/A'}</p>
        </div>
        <div style={{ background: theme.bg, borderRadius: '10px', padding: '24px' }}>
          <h4 style={{ color: theme.text, fontSize: '16px', fontWeight: 600, margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Timing Analysis</h4>
          <p style={{ color: theme.textMuted, fontSize: '17px', lineHeight: '1.8', margin: 0 }}>{timingAnalysis.trim() || 'N/A'}</p>
        </div>
      </div>

      {/* Key Issues - 去掉红色背景，只保留红色标题，用 bullet points */}
      {keyIssues.length > 0 && (
        <div style={{
          background: theme.bg,
          borderRadius: '10px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h4 style={{
            color: theme.red,
            fontSize: '16px',
            fontWeight: 600,
            margin: '0 0 16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Key Issues
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {keyIssues.map((issue, i) => (
              <li key={i} style={{
                color: theme.textMuted,
                fontSize: '17px',
                lineHeight: '1.8',
                marginBottom: '12px',
                paddingLeft: '8px'
              }}>
                {issue.trim()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div style={{ background: theme.bg, borderRadius: '10px', padding: '24px' }}>
          <h4 style={{
            color: theme.text,
            fontSize: '16px',
            fontWeight: 600,
            margin: '0 0 16px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Suggestions
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            {suggestions.map((s, i) => (
              <li key={i} style={{
                color: theme.textMuted,
                fontSize: '17px',
                lineHeight: '1.8',
                marginBottom: '12px',
                paddingLeft: '8px'
              }}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ==================== 主页面组件 ====================
export function PortfolioReviewPage({ onBack, onUpgrade, onAnalysis, onFeedback }) {
  const { user, userData } = useAuth();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [result, setResult] = useState(null);
  const [stockCharts, setStockCharts] = useState([]);
  const [error, setError] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);

  // Portfolio Review: paid users or trial users with access
  const isProPlus = !!user;


  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const droppedFile = e.dataTransfer.files[0]; if (droppedFile) handleFile(droppedFile); };
  const handleFileSelect = (e) => { const selectedFile = e.target.files[0]; if (selectedFile) handleFile(selectedFile); };
  const handleFile = (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'pdf', 'png', 'jpg', 'jpeg'].includes(ext)) { setError('Please upload a CSV, PDF, or image file'); return; }
    setFile(f); setError(null); setResult(null); setStockCharts([]);
  };

  const analyzeFile = async () => {
    //if (!file || !isProPlus) return;
    if (!file || !user) return;
    setLoading(true); setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', user?.uid || 'test_user');
      formData.append('tier', userData?.tier || 'free');

      const response = await fetch('https://insightstock-api-358482645476.us-west2.run.app/api/portfolio/upload', { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Analysis failed');

      const data = await response.json();
      setResult(data);
      setLoading(false);

      // 保存到历史记录
      if (user) {
        await addPortfolioHistory(user.uid, {
          fileName: file.name,
          symbols: data.symbols || [],
          totalTrades: data.total_trades || 0,
          netFlow: data.net_flow || 0
        });
      }

      if (data.symbols && data.symbols.length > 0) {
        setLoadingCharts(true);
        const chartPromises = data.symbols.map(async (symbol) => {
          try {
            const chartResponse = await fetch('https://insightstock-api-358482645476.us-west2.run.app/api/portfolio/analyze-stock', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ symbol, trades: data.trades, user_id: user?.uid || 'test_user' })
            });
            if (chartResponse.ok) return await chartResponse.json();
            return null;
          } catch (err) { console.error('Failed to load chart for ' + symbol, err); return null; }
        });
        const charts = await Promise.all(chartPromises);
        setStockCharts(charts.filter(c => c !== null));
        setLoadingCharts(false);
      }
    } catch (err) { setError(err.message || 'Failed to analyze file'); setLoading(false); }
  };

  if (!isProPlus) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: '-apple-system, sans-serif', color: theme.text, padding: '28px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: '2px solid ' + theme.border, borderRadius: '10px', padding: '16px 28px', color: theme.textMuted, fontSize: '20px', cursor: 'pointer', marginBottom: '40px' }}>← Back</button>
          <div style={{ textAlign: 'center', padding: '60px 40px' }}>
            <div style={{ fontSize: '80px', marginBottom: '24px' }}>📊</div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '48px', fontWeight: 400, color: theme.text, margin: '0 0 16px' }}>Portfolio Review</h1>
            <p style={{ color: theme.textMuted, fontSize: '22px', margin: '0 0 32px', lineHeight: '1.6' }}>Upload your trading history and get AI-powered analysis.</p>
            <div style={{ background: theme.bgCard, border: '2px solid ' + theme.purple, borderRadius: '20px', padding: '40px', marginBottom: '32px' }}>
              <h3 style={{ color: theme.purple, fontSize: '28px', margin: '0 0 24px' }}>Pro+ Feature</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', textAlign: 'left', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                {['Upload CSV, PDF, or screenshots', 'AI analyzes your trade timing', 'See buy/sell vs market sentiment', 'Get personalized improvement tips', '20 analyses per day'].map((item, i) => <li key={i} style={{ color: theme.textMuted, fontSize: '20px', padding: '12px 0', display: 'flex', alignItems: 'center', gap: '14px' }}><span style={{ color: theme.purple }}>✓</span> {item}</li>)}
              </ul>
              <button onClick={onUpgrade} style={{ background: theme.purple, border: 'none', borderRadius: '12px', padding: '20px 48px', color: '#fff', fontSize: '22px', fontWeight: 700, cursor: 'pointer' }}>Upgrade to Pro+ →</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: '-apple-system, sans-serif', color: theme.text, padding: '28px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid ' + theme.border }}>
          <button onClick={onBack} style={{ background: 'transparent', border: '2px solid ' + theme.border, borderRadius: '10px', padding: '16px 28px', color: theme.textMuted, fontSize: '20px', cursor: 'pointer' }}>← Back</button>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '36px', fontWeight: 400, color: theme.text, margin: 0, flex: 1 }}>Portfolio Review</h1>
          <button onClick={onFeedback} style={{ background: 'transparent', border: 'none', color: theme.textMuted, fontSize: '16px', cursor: 'pointer', padding: '8px 16px' }}>Feedback</button>
        </div>

        {/* SEO Description */}
        <p style={{ color: theme.textDark, fontSize: '18px', margin: '0 0 24px', fontStyle: 'italic' }}>
          AI portfolio analysis that compares your trades with market sentiment and earnings signals. Upload your trade history to identify behavioral patterns and timing gaps.
        </p>

        {!result && !loading && (
          <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} style={{ background: dragOver ? 'rgba(45, 212, 191, 0.1)' : theme.bgCard, border: '2px dashed ' + (dragOver ? theme.teal : theme.border), borderRadius: '20px', padding: '60px 40px', textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>📁</div>
            <h2 style={{ color: theme.text, fontSize: '28px', margin: '0 0 12px', fontWeight: 500 }}>{file ? file.name : 'Drop your file here'}</h2>
            <p style={{ color: theme.textDark, fontSize: '18px', margin: '0 0 16px' }}>Supports: CSV, PNG, JPG, PDF</p>
            <p style={{ color: theme.neutral, fontSize: '14px', margin: '0 0 24px' }}>📌 Screenshots must include: Date, Symbol, Buy/Sell, Quantity, Price</p>
            <input type="file" accept=".csv,.pdf,.png,.jpg,.jpeg" onChange={handleFileSelect} style={{ display: 'none' }} id="file-upload" />
            <label htmlFor="file-upload" style={{ display: 'inline-block', background: 'transparent', border: '2px solid ' + theme.teal, borderRadius: '10px', padding: '16px 36px', color: theme.teal, fontSize: '18px', fontWeight: 600, cursor: 'pointer' }}>Browse Files</label>
            {file && <div style={{ marginTop: '32px' }}><button onClick={analyzeFile} disabled={loading} style={{ background: theme.teal, border: 'none', borderRadius: '12px', padding: '20px 48px', color: '#0f172a', fontSize: '22px', fontWeight: 700, cursor: loading ? 'wait' : 'pointer' }}>{loading ? 'Analyzing...' : 'Analyze My Trades →'}</button></div>}
          </div>
        )}

        {error && <div style={{ background: 'rgba(248, 81, 73, 0.1)', border: '1px solid ' + theme.red, borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'center' }}><p style={{ color: theme.red, fontSize: '18px', margin: 0 }}>{error}</p></div>}

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{ width: '60px', height: '60px', border: '3px solid ' + theme.border, borderTop: '3px solid ' + theme.teal, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 24px' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: theme.text, fontSize: '24px', margin: '0 0 8px' }}>Analyzing your trades...</p>
            <p style={{ color: theme.textDark, fontSize: '18px', margin: '0 0 8px' }}>This may take 30 seconds to 1 minute.</p>
            <p style={{ color: theme.textDark, fontSize: '14px', fontStyle: 'italic' }}>Thanks for your patience — I'll get faster as I grow 🌱</p>
          </div>
        )}

        {result && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '28px' }}>
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '24px' }}><p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 8px', textTransform: 'uppercase' }}>Total Trades</p><p style={{ color: theme.text, fontSize: '32px', fontWeight: 700, margin: 0 }}>{result.total_trades || 0}</p></div>
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '24px' }}><p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 8px', textTransform: 'uppercase' }}>Total Buy</p><p style={{ color: theme.red, fontSize: '32px', fontWeight: 700, margin: 0 }}>${(result.total_buy || 0).toLocaleString()}</p></div>
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '24px' }}><p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 8px', textTransform: 'uppercase' }}>Total Sell</p><p style={{ color: theme.green, fontSize: '32px', fontWeight: 700, margin: 0 }}>${(result.total_sell || 0).toLocaleString()}</p></div>
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '24px' }}><p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 8px', textTransform: 'uppercase' }}>Net Flow</p><p style={{ color: (result.net_flow || 0) >= 0 ? theme.green : theme.red, fontSize: '32px', fontWeight: 700, margin: 0 }}>{(result.net_flow || 0) >= 0 ? '+' : ''}${(result.net_flow || 0).toLocaleString()}</p></div>
            </div>

            {result.metrics && <MetricsDisplay metrics={result.metrics} />}

            {loadingCharts && <div style={{ textAlign: 'center', padding: '40px' }}><div style={{ width: '40px', height: '40px', border: '3px solid ' + theme.border, borderTop: '3px solid ' + theme.purple, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} /><p style={{ color: theme.textMuted, fontSize: '18px', margin: '0 0 8px' }}>Loading stock charts...</p><p style={{ color: theme.textDark, fontSize: '14px', fontStyle: 'italic' }}>Thanks for your patience — I'll get faster as I grow 🌱</p></div>}

            {stockCharts.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <h2 style={{ fontFamily: 'Georgia, serif', color: theme.text, fontSize: '28px', fontWeight: 400, margin: 0 }}>Stock Analysis</h2>
                  <select value={selectedStock || stockCharts[0]?.symbol} onChange={(e) => setSelectedStock(e.target.value)} style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '8px', padding: '12px 16px', color: theme.text, fontSize: '18px', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                    {stockCharts.map(chart => <option key={chart.symbol} value={chart.symbol}>{chart.symbol}</option>)}
                  </select>
                </div>
                {stockCharts.filter(c => c.symbol === (selectedStock || stockCharts[0]?.symbol)).map((chart) => <StockChart key={chart.symbol} symbol={chart.symbol} candles={chart.candles} sentimentZones={chart.sentiment_zones} tradeMarkers={chart.trade_markers} analysis={chart.analysis} />)}
              </div>
            )}

            {result.analysis && (
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '32px', marginBottom: '28px' }}>
                <h3 style={{ fontFamily: 'Georgia, serif', color: theme.teal, fontSize: '28px', fontWeight: 400, margin: '0 0 24px' }}>Overall Analysis</h3>
                <OverallAnalysisContent analysis={result.analysis} />
                <p style={{ color: theme.textDark, fontSize: '14px', margin: '24px 0 0', textAlign: 'right', fontStyle: 'italic' }}>AI-generated insights. May contain errors.</p>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button onClick={() => { setFile(null); setResult(null); setStockCharts([]); setError(null); }} style={{ background: 'transparent', border: '2px solid ' + theme.teal, borderRadius: '12px', padding: '16px 36px', color: theme.teal, fontSize: '18px', fontWeight: 600, cursor: 'pointer' }}>Analyze Another File</button>
            </div>
          </>
        )}

        {!result && !loading && (
          <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '32px' }}>
            <h3 style={{ color: theme.text, fontSize: '22px', margin: '0 0 20px' }}>How to export your trades:</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div><h4 style={{ color: theme.teal, fontSize: '18px', margin: '0 0 12px' }}>Robinhood</h4><p style={{ color: theme.textDark, fontSize: '16px', lineHeight: '1.6', margin: 0 }}>Account → Statements & History → Download CSV</p></div>
              <div><h4 style={{ color: theme.teal, fontSize: '18px', margin: '0 0 12px' }}>Webull</h4><p style={{ color: theme.textDark, fontSize: '16px', lineHeight: '1.6', margin: 0 }}>Mobile App: Account → History → Filter → Download (sent to email)</p></div>
              <div><h4 style={{ color: theme.teal, fontSize: '18px', margin: '0 0 12px' }}>Fidelity</h4><p style={{ color: theme.textDark, fontSize: '16px', lineHeight: '1.6', margin: 0 }}>Accounts & Trade → Activity & Orders → History → Download</p></div>
              <div><h4 style={{ color: theme.teal, fontSize: '18px', margin: '0 0 12px' }}>Screenshot</h4><p style={{ color: theme.textDark, fontSize: '16px', lineHeight: '1.6', margin: 0 }}>Take a screenshot of your trade history page</p></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PortfolioReviewPage;
